/**
 * Shared chart theme for the Analytics section (TASK-3835).
 *
 * One source of truth for chart colors, bar geometry, axis sizing, and value
 * formatting so every Recharts chart under `features/reports` looks cohesive
 * and matches the app's brand/emerald/amber/rose system. This is a plain `.ts`
 * module (no JSX) so it can be imported by any chart without tripping the
 * react-refresh/only-export-components rule and without circular deps.
 *
 * The companion `ChartTooltip.tsx` and `ChartEmpty.tsx` components consume these
 * constants/helpers.
 */

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------

/** Plan / muted baseline series — slate-300. */
export const CHART_BASELINE = "#cbd5e1";
/** Primary / "Факт" series — brand-primary (web/tailwind.config.js). */
export const CHART_PRIMARY = "#0073EA";
/** Positive / success accent — emerald-500. */
export const CHART_POSITIVE = "#10b981";
/** Warning accent — amber-500. */
export const CHART_WARNING = "#f59e0b";
/** Negative / alert accent — rose-600. */
export const CHART_NEGATIVE = "#e11d48";
/** Light horizontal gridlines — slate-100. */
export const CHART_GRID = "#f1f5f9";
/** Muted tick / axis text — slate-400. */
export const CHART_AXIS = "#94a3b8";

/** All chart colors keyed by role, for callers that prefer object access. */
export const CHART_COLORS = {
  baseline: CHART_BASELINE,
  primary: CHART_PRIMARY,
  positive: CHART_POSITIVE,
  warning: CHART_WARNING,
  negative: CHART_NEGATIVE,
  grid: CHART_GRID,
  axis: CHART_AXIS,
} as const;

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/** Standard chart body height in px (Tailwind h-56). */
export const CHART_HEIGHT = 224;
/** Standard bar corner radius — [topLeft, topRight, bottomRight, bottomLeft]. */
export const BAR_RADIUS: [number, number, number, number] = [6, 6, 0, 0];
/** Cap individual bar width so single-row charts don't render full-width slabs. */
export const MAX_BAR_SIZE = 48;
/** Gap between bar categories (Recharts `barCategoryGap`). */
export const BAR_CATEGORY_GAP = "30%";
/** Gap between bars within a category (Recharts `barGap`), in px. */
export const BAR_GAP = 6;
/** Default Y-axis width — wide enough that the first tick digit isn't clipped. */
export const Y_AXIS_WIDTH = 44;
/** Y-axis width for percent axes (e.g. "100%"). */
export const Y_AXIS_WIDTH_PCT = 40;
/** Distance between axis ticks and their labels, in px. */
export const TICK_MARGIN = 6;
/**
 * Standard chart margin. Note `left: 0` (not the buggy `-16` previously used
 * per-chart, which clipped Y-axis ticks — this is the fix the per-chart tasks
 * 3836–3838 will adopt).
 */
export const CHART_MARGIN = { top: 16, right: 12, bottom: 0, left: 0 } as const;

/** Shared axis tick style for `tick={...}` props. */
export const AXIS_TICK = { fontSize: 11, fill: CHART_AXIS } as const;

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** Em-dash used everywhere a value is missing. */
export const EM_DASH = "—";

/** Map an i18next language tag to a `Intl` locale, mirroring app-wide usage. */
export function chartLocale(language: string | undefined): string {
  return language === "ru" ? "ru-RU" : "en-GB";
}

export interface FormatNumberOptions {
  /** i18next language (e.g. `i18n.language`); falls back to en-GB grouping. */
  language?: string;
  /**
   * Unit suffix. Word units ("км", "ч", "€") are appended after a space;
   * "%" is appended tight (`95%`).
   */
  unit?: string;
  /** Minimum fraction digits (default 0). */
  minimumFractionDigits?: number;
  /** Maximum fraction digits (default 1). */
  maximumFractionDigits?: number;
}

/**
 * Locale-aware number formatter with thousands grouping. `null`/`undefined`/
 * `NaN` render as an em-dash. An optional unit suffix is appended after a thin
 * space (`28450 → "28 450 км"`). Reused by axis tickFormatters, LabelList
 * formatters, and the shared tooltip.
 */
export function formatNumber(
  value: number | null | undefined,
  opts: FormatNumberOptions = {},
): string {
  if (value == null || Number.isNaN(value)) {
    return EM_DASH;
  }
  const {
    language,
    unit,
    minimumFractionDigits = 0,
    maximumFractionDigits = 1,
  } = opts;
  const formatted = new Intl.NumberFormat(chartLocale(language), {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
  if (!unit) {
    return formatted;
  }
  // `%` sits tight against the number; word-units get a separating space.
  return unit === "%" ? `${formatted}%` : `${formatted} ${unit}`;
}

/**
 * Convenience wrapper for percent values — appends `%` with no fraction digits
 * by default. `null`/`undefined`/`NaN` render as an em-dash.
 */
export function formatPercent(
  value: number | null | undefined,
  opts: Omit<FormatNumberOptions, "unit"> = {},
): string {
  return formatNumber(value, {
    maximumFractionDigits: 0,
    ...opts,
    unit: "%",
  });
}
